import { useState, useEffect, useRef } from 'react';
import {
  Table,
  Button,
  Dialog,
  Form,
  Input,
  InputNumber,
  Select,
  Popconfirm,
  Tag,
  Space,
  Image,
  ImageViewer,
  MessagePlugin,
  type PrimaryTableCol
} from 'tdesign-react';
import { CheckIcon, CloseIcon, EditIcon, DeleteIcon } from 'tdesign-icons-react';
import { productApi } from '../../api';
import type { Product } from '../../types';
// 引入拆分后的组件
import RichTextEditor from '../../components/RichTextEditor';
import CoverImageUploader from '../../components/CoverImageUploader';

const statusOptions = [
  { label: '待审核', value: 0, theme: 'warning' },
  { label: '已上架', value: 1, theme: 'success' },
  { label: '已下架', value: 2, theme: 'default' },
  { label: '审核拒绝', value: 3, theme: 'danger' },
];

export default function ProductAudit() {
  const [data, setData] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [dialogVisible, setDialogVisible] = useState(false);
  const [auditDialogVisible, setAuditDialogVisible] = useState(false);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewImages, setPreviewImages] = useState<string[]>([]);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [auditingProduct, setAuditingProduct] = useState<Product | null>(null);
  const [auditRemark, setAuditRemark] = useState('');
  const [categories, setCategories] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState<'basic' | 'content'>('basic');
  // 【修复】使用本地状态管理自定义组件的值
  const [content, setContent] = useState('');
  const [mainImageUrl, setMainImageUrl] = useState('');
  
  // 【修复】创建 Form 引用，用于调用 validate 等方法
  const formRef = useRef<any>(null);

  const columns: PrimaryTableCol<Product>[] = [
    { colKey: 'id', title: 'ID', width: 80 },
    {
      colKey: 'mainImage',
      title: '图片',
      width: 80,
      cell: ({ row }: any) => (
        row.mainImage ? (
          <Image
            src={getFirstImage(row.mainImage)}
            style={{ width: 50, height: 50, objectFit: 'cover', cursor: 'pointer' }}
            onClick={() => handlePreviewImage([row.mainImage])}
          />
        ) : (
          <div className="w-12 h-12 bg-gray-100 flex items-center justify-center text-gray-400">无</div>
        )
      ),
    },
    { colKey: 'productName', title: '商品名称', ellipsis: true },
    { colKey: 'productCode', title: '商品编码', width: 120 },
    { colKey: 'category', title: '分类', width: 100 },
    { colKey: 'price', title: '价格', width: 100, cell: ({ row }: any) => `¥${row.price}` },
    { colKey: 'stock', title: '库存', width: 80 },
    { colKey: 'supplierName', title: '供应商', width: 150 },
    {
      colKey: 'status',
      title: '状态',
      width: 100,
      cell: ({ row }: any) => {
        const status = statusOptions.find(s => s.value === row.status);
        return <Tag theme={status?.theme as any}>{status?.label || '未知'}</Tag>;
      },
    },
    { colKey: 'viewCount', title: '浏览', width: 80 },
    { colKey: 'salesCount', title: '销量', width: 80 },
    {
      colKey: 'action',
      title: '操作',
      width: 400,
      fixed: 'right',
      cell: ({ row }: any) => (
        <Space>
          {row.status === 0 && (
            <>
              <Button
                theme="success"
                variant="text"
                icon={<CheckIcon />}
                onClick={() => handleAudit(row, 1)}
              >
                通过
              </Button>
              <Button
                theme="danger"
                variant="text"
                icon={<CloseIcon />}
                onClick={() => handleAudit(row, 3)}
              >
                拒绝
              </Button>
            </>
          )}
          {row.status === 1 && (
            <Button
              theme="warning"
              variant="text"
              onClick={() => handleToggleStatus(row.id, 2)}
            >
              下架
            </Button>
          )}
          {row.status === 2 && (
            <Button
              theme="success"
              variant="text"
              onClick={() => handleToggleStatus(row.id, 1)}
            >
              上架
            </Button>
          )}
          <Button
            theme="primary"
            variant="text"
            icon={<EditIcon />}
            onClick={() => handleEdit(row)}
          >
            编辑
          </Button>
          <Popconfirm
            content="确定要删除该商品吗？"
            onConfirm={() => handleDelete(row.id)}
          >
            <Button theme="danger" variant="text" icon={<DeleteIcon />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const fetchData = async (page = pagination.current, pageSize = pagination.pageSize) => {
    setLoading(true);
    try {
      const res = await productApi.getList({ pageNum: page, pageSize });
      if (res.code === 200) {
        setData(res.data.list);
        setPagination({
          current: res.data.pageNum,
          pageSize: res.data.pageSize,
          total: res.data.total,
        });
      }
    } catch (err) {
      MessagePlugin.error('加载数据失败');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await productApi.getCategories();
      if (res.code === 200) {
        setCategories(res.data);
      }
    } catch (err) {
      console.error('加载分类失败:', err);
    }
  };

  useEffect(() => {
    fetchData();
    fetchCategories();
  }, []);

  const getFirstImage = (mainImage: string | string[] | undefined | null): string => {
    if (!mainImage) return 'https://via.placeholder.com/40';
    
    let firstUrl = '';
    if (Array.isArray(mainImage)) {
      firstUrl = mainImage[0] || '';
    } else if (typeof mainImage === 'string') {
      const urls = mainImage.split(',');
      firstUrl = urls.length > 0 ? urls[0].trim() : '';
    }
    
    return firstUrl || 'https://via.placeholder.com/40';
  };
  
  const handlePreviewImage = (images: string[]) => {
    setPreviewImages(images);
    setPreviewVisible(true);
  };

  // 【修复】简化 handleEdit，同步更新本地状态
  const handleEdit = (product: Product) => {
    setEditingProduct(product);
    setActiveTab('basic');
    // 1. 更新本地状态（用于自定义组件显示）
    setContent(product.description || '');
    setMainImageUrl(product.mainImage || '');

    // 2. 打开对话框
    // 由于 Dialog 增加了 key，重新打开时会重新挂载，Form 会重新读取 initialData
    setDialogVisible(true);
  };

  const handleAudit = (product: Product, status: number) => {
    setAuditingProduct(product);
    setAuditRemark('');
    setAuditDialogVisible(true);
  };

  const handleAuditSubmit = async (status: number) => {
    if (!auditingProduct) return;
    
    try {
      const res = await productApi.audit({
        id: auditingProduct.id,
        status,
        remark: auditRemark,
      });
      
      if (res.code === 200) {
        MessagePlugin.success(status === 1 ? '审核通过' : '审核拒绝');
        setAuditDialogVisible(false);
        fetchData();
      } else {
        MessagePlugin.error(res.message || '审核失败');
      }
    } catch (err) {
      MessagePlugin.error('审核失败');
    }
  };

  const handleToggleStatus = async (id: number, status: number) => {
    try {
      const res = await productApi.toggleStatus(id, status);
      if (res.code === 200) {
        MessagePlugin.success(status === 1 ? '上架成功' : '下架成功');
        fetchData();
      } else {
        MessagePlugin.error(res.message || '操作失败');
      }
    } catch (err) {
      MessagePlugin.error('操作失败');
    }
  };

  const handleDelete = async (id: number) => {
    try {
      const res = await productApi.delete(id);
      if (res.code === 200) {
        MessagePlugin.success('删除成功');
        fetchData();
      } else {
        MessagePlugin.error(res.message || '删除失败');
      }
    } catch (err) {
      MessagePlugin.error('删除失败');
    }
  };

  const handleSubmit = async () => {
    try {
      // 【修复】通过 formRef 获取实例并验证
      const result = await formRef.current?.validate();
      
      // validate 返回 true 表示成功，或者包含错误信息
      if (result !== true) {
        return;
      }

      // 获取 Form 中的标准字段值
      // TDesign Form getFieldsValue 获取当前表单所有字段的值
      const values = formRef.current?.getFieldsValue(true) || {};
      
      // 【重要】确保自定义组件的最新状态被包含在提交数据中
      const submitData = {
        ...values,
        description: content,
        mainImage: mainImageUrl,
      };

      let res;
      if (editingProduct) {
        res = await productApi.update(editingProduct.id, submitData);
      } else {
        res = await productApi.create(submitData);
      }

      if (res.code === 200) {
        MessagePlugin.success(editingProduct ? '更新成功' : '创建成功');
        setDialogVisible(false);
        // 重置编辑状态，以便下次新增时为空
        setEditingProduct(null);
        fetchData();
      } else {
        MessagePlugin.error(res.message || '操作失败');
      }
    } catch (err) {
      console.error('验证失败或提交错误:', err);
      MessagePlugin.error('表单验证失败或提交出错');
    }
  };

  const categoryOptions = categories.map(c => ({ label: c, value: c }));

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">商品审核</h1>
      </div>

      <Table
        data={data}
        columns={columns}
        rowKey="id"
        loading={loading}
        pagination={{
          ...pagination,
          onChange: (pageInfo: any) => fetchData(pageInfo.current, pageInfo.pageSize),
        }}
      />

      {/* 
        【关键修复】添加 key 属性 
        当 editingProduct 变化时（包括从 null 变为对象，或从一个对象变为另一个），
        React 会销毁旧的 Dialog 并创建新的，从而彻底重置 Form 内部状态和子组件状态。
      */}
      <Dialog
        key={editingProduct ? `edit-${editingProduct.id}` : 'create-new'}
        header={editingProduct ? '编辑商品' : '新增商品'}
        visible={dialogVisible}
        onClose={() => {
          setDialogVisible(false);
          setEditingProduct(null); // 关闭时也重置
        }}
        onConfirm={handleSubmit}
        width={900}
      >
        <div className="mb-4 border-b">
          <div className="flex">
            <button
              className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${activeTab === 'basic' ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
              onClick={() => setActiveTab('basic')}
            >
              基本信息
            </button>
            <button
              className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${activeTab === 'content' ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
              onClick={() => setActiveTab('content')}
            >
              商品描述
            </button>
          </div>
        </div>
        <div style={{ display: activeTab === 'basic' ? 'block' : 'none' }}>
          {/* 【修复】绑定 ref 和 initialData */}
          <Form 
            ref={formRef}
            initialData={editingProduct || {}} 
            labelWidth={100}
          >
            <Form.FormItem label="商品名称" name="productName" rules={[{ required: true, message: '请输入商品名称' }]}>
              <Input placeholder="请输入商品名称" />
            </Form.FormItem>
            <Form.FormItem label="商品编码" name="productCode">
              <Input placeholder="请输入商品编码" />
            </Form.FormItem>
            <Form.FormItem label="分类" name="category">
              <Select options={categoryOptions} placeholder="请选择分类" />
            </Form.FormItem>
            <Form.FormItem label="价格" name="price" rules={[{ required: true, message: '请输入价格' }]}>
              <InputNumber placeholder="请输入价格" min={0} precision={2} />
            </Form.FormItem>
            <Form.FormItem label="库存" name="stock" rules={[{ required: true, message: '请输入库存' }]}>
              <InputNumber placeholder="请输入库存" min={0} />
            </Form.FormItem>
            <Form.FormItem label="单位" name="unit">
              <Input placeholder="请输入单位" />
            </Form.FormItem>
            
            {/* 【修复】使用本地状态 mainImageUrl 和 setMainImageUrl */}
            <Form.FormItem label="主图" name="mainImage">
              <CoverImageUploader 
                value={mainImageUrl} 
                onChange={(val) => {
                  setMainImageUrl(Array.isArray(val) ? val[0] || '' : val);
                }} 
              />
            </Form.FormItem>
          </Form>
        </div>
        <div style={{ display: activeTab === 'content' ? 'block' : 'none' }}>
          <RichTextEditor value={content} 
                onChange={(val) => {
                  setContent(val);
                }} />
        </div>
      </Dialog>

      {/* 审核对话框 */}
      <Dialog
        header="审核商品"
        visible={auditDialogVisible}
        onClose={() => setAuditDialogVisible(false)}
        footer={
          <div className="flex justify-end gap-3">
            <Button onClick={() => setAuditDialogVisible(false)}>取消</Button>
            <Button theme="danger" onClick={() => handleAuditSubmit(3)}>拒绝</Button>
            <Button theme="success" onClick={() => handleAuditSubmit(1)}>通过</Button>
          </div>
        }
      >
        <div className="py-4">
          <p className="mb-2">商品：{auditingProduct?.productName}</p>
          <p className="mb-4">供应商：{auditingProduct?.supplierName}</p>
          <div className="mb-2">审核备注：</div>
          <Input
            value={auditRemark}
            onChange={(value) => setAuditRemark(value)}
            placeholder="请输入审核备注（可选）"
          />
        </div>
      </Dialog>

      {/* 图片预览 */}
      <ImageViewer
        visible={previewVisible}
        images={previewImages}
        onClose={() => setPreviewVisible(false)}
      />
    </div>
  );
}